// One RTCPeerConnection to one other person, with perfect negotiation
// (https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API/Perfect_negotiation).
// The polite side is the one whose user id sorts first (the session decides).
// When both offer at once, the polite side drops its offer and answers, and
// the impolite side ignores the other's offer.
//
// Promise:
// - Candidates that come before the remote description (a renegotiation for a
//   shared screen, a connection made again) are kept and added after it.
// - A connection that fails restarts ICE once and says "connecting" meanwhile;
//   failing again is told as failed.
// - When gathering started and no candidate came within `iceStallMs`,
//   `onIceStall` is called: Chromium sometimes never gathers on a connection
//   made right next to noise suppression's start (restartIce does not bring it
//   back), so the session makes the connection again.
// - Swapping the sent track (noise suppression ready, a microphone taken
//   again) does not renegotiate; adding or removing the screen does, and
//   perfect negotiation settles it.
import type { VoiceSignalData } from "@trameria/cloud-api/voice-shared";

export interface VoicePeerOptions {
  polite: boolean;
  iceServers: RTCIceServer[];
  localStream: MediaStream;
  /** The screen being shared when the connection is made (for who joins later). */
  screenStream?: MediaStream | null;
  send(data: VoiceSignalData): void;
  onRemoteStream(stream: MediaStream): void;
  onRemoteScreen?(stream: MediaStream): void;
  onConnectionState(state: RTCPeerConnectionState): void;
  onIceStall?(): void;
  iceStallMs?: number;
  createPeerConnection?(config: RTCConfiguration): RTCPeerConnection;
}

export const ICE_STALL_MS = 3000;

export class VoicePeer {
  private pc: RTCPeerConnection;
  private makingOffer = false;
  private ignoreOffer = false;
  private settingRemoteAnswer = false;
  private iceRestarted = false;
  private closed = false;
  private stallTimer: ReturnType<typeof setTimeout> | null = null;
  private gotCandidate = false;
  private screenSender: RTCRtpSender | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];

  constructor(private opts: VoicePeerOptions) {
    const create =
      opts.createPeerConnection ??
      ((c: RTCConfiguration) => new RTCPeerConnection(c));
    this.pc = create({ iceServers: opts.iceServers });
    for (const track of opts.localStream.getTracks())
      this.pc.addTrack(track, opts.localStream);
    if (opts.screenStream) this.setScreen(opts.screenStream);

    this.pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true;
        await this.pc.setLocalDescription();
        if (this.closed) return;
        if (this.pc.localDescription)
          opts.send({ description: toPlain(this.pc.localDescription) });
      } catch (error) {
        console.error("voice: offer failed", error);
      } finally {
        this.makingOffer = false;
      }
    };
    this.pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this.gotCandidate = true;
        this.clearStall();
      }
      opts.send({ candidate: candidate ? toPlainCandidate(candidate) : null });
    };
    this.pc.onicegatheringstatechange = () => {
      if (
        this.pc.iceGatheringState === "gathering" &&
        !this.gotCandidate &&
        !this.stallTimer
      )
        this.stallTimer = setTimeout(() => {
          this.stallTimer = null;
          if (!this.closed && !this.gotCandidate) opts.onIceStall?.();
        }, opts.iceStallMs ?? ICE_STALL_MS);
    };
    this.pc.ontrack = (ev) => {
      const stream = ev.streams[0];
      if (!stream) return;
      if (ev.track.kind === "video") opts.onRemoteScreen?.(stream);
      else opts.onRemoteStream(stream);
    };
    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      if (state === "failed" && !this.iceRestarted) {
        this.iceRestarted = true;
        this.pc.restartIce();
        opts.onConnectionState("connecting");
        return;
      }
      opts.onConnectionState(state);
    };
  }

  async handleSignal(data: VoiceSignalData): Promise<void> {
    if (this.closed) return;
    try {
      if ("description" in data) {
        const description = data.description;
        const readyForOffer =
          !this.makingOffer &&
          (this.pc.signalingState === "stable" || this.settingRemoteAnswer);
        const collision = description.type === "offer" && !readyForOffer;
        this.ignoreOffer = !this.opts.polite && collision;
        if (this.ignoreOffer) return;
        try {
          this.settingRemoteAnswer = description.type === "answer";
          await this.pc.setRemoteDescription(
            description as RTCSessionDescriptionInit,
          );
        } finally {
          this.settingRemoteAnswer = false;
        }
        if (this.closed) return;
        for (const c of this.pendingCandidates.splice(0))
          await this.addCandidate(c);
        if (this.closed) return;
        if (description.type === "offer") {
          await this.pc.setLocalDescription();
          if (this.closed) return;
          if (this.pc.localDescription)
            this.opts.send({ description: toPlain(this.pc.localDescription) });
        }
        return;
      }
      if (data.candidate === null) return;
      if (!this.pc.remoteDescription) {
        this.pendingCandidates.push(data.candidate as RTCIceCandidateInit);
        return;
      }
      await this.addCandidate(data.candidate as RTCIceCandidateInit);
    } catch (error) {
      console.error("voice: signal failed", error);
    }
  }

  private async addCandidate(c: RTCIceCandidateInit): Promise<void> {
    try {
      await this.pc.addIceCandidate(c);
    } catch (error) {
      // A candidate of an ignored offer is expected to fail.
      if (!this.ignoreOffer) throw error;
    }
  }

  /** Swaps the sent audio track (no renegotiation). */
  async replaceTrack(track: MediaStreamTrack): Promise<void> {
    if (this.closed) return;
    try {
      await Promise.all(
        this.pc
          .getSenders()
          .filter(
            (s) =>
              s !== this.screenSender &&
              (s.track === null || s.track.kind === track.kind),
          )
          .map((s) => s.replaceTrack(track)),
      );
    } catch (error) {
      console.error("voice: replaceTrack failed", error);
    }
  }

  /** Adds the shared screen's video, or removes it (null). */
  setScreen(stream: MediaStream | null): void {
    if (this.closed) return;
    if (this.screenSender) {
      try {
        this.pc.removeTrack(this.screenSender);
      } catch (error) {
        console.error("voice: removeTrack failed", error);
      }
      this.screenSender = null;
    }
    const track = stream?.getVideoTracks()[0];
    if (stream && track) this.screenSender = this.pc.addTrack(track, stream);
  }

  private clearStall() {
    if (this.stallTimer) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  }

  close(): void {
    this.closed = true;
    this.clearStall();
    this.pc.onnegotiationneeded = null;
    this.pc.onicecandidate = null;
    this.pc.onicegatheringstatechange = null;
    this.pc.ontrack = null;
    this.pc.onconnectionstatechange = null;
    this.pc.close();
  }
}

/** A description as JSON carries more than type and sdp; send only those. */
function toPlain(d: RTCSessionDescriptionInit | RTCSessionDescription) {
  return {
    type: d.type as "offer" | "answer" | "pranswer" | "rollback",
    sdp: d.sdp,
  };
}

function toPlainCandidate(c: RTCIceCandidate | RTCIceCandidateInit) {
  return {
    candidate: c.candidate ?? undefined,
    sdpMid: c.sdpMid ?? undefined,
    sdpMLineIndex: c.sdpMLineIndex ?? undefined,
    usernameFragment: c.usernameFragment ?? undefined,
  };
}
